/**
     * Alert non-modal dialog used for showing messages to the user. Could be inserted
     * in different elements on the page. So it could be styled and positioned differently.
     * It has a timeout for auto-closing. Could be extened to use with Manager.
     *
     * @constructor
     * @param {AlertConfig} alertConfig - The configuration object for setting up the alert dialog.
     *
     * @typedef {Object} CtaItem
     * @property {string} text - The text displayed on the CTA button.
     * @property {Function} callback - The function to be executed when the CTA button is clicked.
     * @property {string} [ariaLabel] - Optional. Accessible name for the button (visible label can be aria-hidden).
     *
     * @typedef {Object} AlertItem
     * @property {string} text - The main text for the alert item.
     * @property {string[]} customCSSClass - An array of CSS classes applied to the alert item.
     * @property {Function} callback - A function executed when the alert item is clicked.
     * @property {CtaItem[]} cta - An array of call-to-action buttons, each with a text and callback.
     * @property {string} icon - HTML string representing an icon (e.g., an image) displayed with the alert item.
     *
     * @typedef {Object} AlertConfig
     * @property {'dialog'|'alert'} type - dialog = modal listbox; alert = role alert (live region), no sr-only title on root, no root tabindex; use CTA aria-label for the spoken message.
     * @property {number} alertTimeout - Optional. The time of displaying the alert until closed automatically. Default: 2500
     * @property {AlertItem[]} items - An array of alert items, each containing text, CSS classes, callback functions, CTAs, and an icon.
     * @property {Element} focusAfterClose - The DOM element that should regain focus after the alert dialog is closed.
     * @property {string[]} customCSSClass - An array of CSS classes applied to the alert dialog itself.
     * @property {string} style - Inline styles applied to the alert dialog for positioning or additional styling.
     * @property {boolean} remainOnScroll - Optional. Determines if alert keeps on showing when page is scrolled. Default: false
     * @property {string} [ariaLabel] - Optional. Sets aria-label on the root; sr-only title is omitted (not used with type alert).
     *
     * @type {AlertConfig}
     * @example
     * const alertConfig = {
     *    alertTimeout: 2500,
     *    type: 'dialog',
     *    items: [
     *              {
     *                  text: 'Removed from Wishlist',
     *                  customCSSClass: [''],
     *                  callback: () => console.log('Alert clicked'),
     *                  cta: [
     *                          { text: 'OK', callback: () => console.log('OK clicked') },
     *                          { text: 'Cancel', callback: () => console.log('Cancel clicked') }
     *                        ],
     *                  icon: '<img src="product.png" alt="product-name" width=60 height=75 />'
     *              }
     *    ],
     *    focusAfterClose: queryFirst('.header-button'),
     *    customCSSClass: ['m-modifier'],
     *    style: '',
     *    remainOnScroll: false
     *  };
     *  new AlertDialog(alertConfig);
     */
const { queryFirst, queryAll } = require('../domUtil');
const {
    EVENT_KEY_ENTER,
    EVENT_KEY_SPACE,
    EVENT_KEY_DOWN,
    EVENT_KEY_UP,
    EVENT_KEY_HOME,
    EVENT_KEY_END,
    EVENT_KEY_ESC,
    KBD_CLASS
} = require('../constants');
const SHEET_SCROLLABLE = '.sheet.show .sheet-body-content';
const MODAL_SCROLLABLE = '.modal.show .modal-body';
const SCROLLABLE_IN_OVERLAY = `${SHEET_SCROLLABLE}, ${MODAL_SCROLLABLE}`;

export class AlertDialog {
    checkIcon = [
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">',
        '<path d="M1 6.4646L4.5 9.9646L11 3.4646" stroke="black"/>',
        '</svg>'
    ].join('\n');

    static _open = new Set();

    static closeAll(except = null, opts = {}) {
        for (const inst of AlertDialog._open) {
            if (inst !== except) inst.closeAlert(opts);
        }
    }

    /**
     * Restart auto-close timers for all open dialogs (soft close).
     * Used when we want "close by timeout" instead of instant close.
     *
     * @param {AlertDialog|null} except - instance to skip (optional)
     */
    static restartTimers(except = null) {
        for (const inst of AlertDialog._open) {
            if (inst !== except) {
                inst.startTimer();
            }
        }
    }

    constructor(alertConfig) {
        this.alertConfig = alertConfig;
        this.alertHost = this.alertConfig.host || document.body;
        this.alert = null;
        this.timeout = null;

        // "private" instance fields
        this._scrollTargets = [];
        this._onAnyScroll = null;
        this._onListboxClick = null;
        this._onEscPreferAlert = null;
        this._onListboxKeydown = null;
        this._onListboxPointerEnter = null;
        this._onAlertKeydown = null;

        // modality:
        this._kbdMode = !!alertConfig.kbdMode;

        // ensure only one alert instance at a time, do not touch focus of previous
        AlertDialog.closeAll(null, { instant: true, restoreFocus: false });

        this.createAlert();

        AlertDialog._open.add(this);
    }

    createAlert() {
        if (!this.alertConfig) {
            console.error('Alert not configured');
            return;
        }
        const titleID = `alert-dialog-title-${this.generateRandomString()}`;
        const alert = document.createElement('div');
        const innerList = document.createElement('div');

        const skipSrHeading = this.alertConfig.type === 'alert';

        if (this.alertConfig.type === 'alert') {
            alert.setAttribute('role', 'alert');
            alert.setAttribute('aria-modal', 'false');
        } else if (this.alertConfig.type === 'dialog') {
            alert.setAttribute('role', 'dialog');
            alert.setAttribute('aria-modal', 'true');
        }

        alert.classList.add('alert-dialog');
        alert.setAttribute('style', this.alertConfig.style || '');

        if (this.alertConfig.ariaLabel) {
            alert.setAttribute('aria-label', this.alertConfig.ariaLabel);
            alert.setAttribute('tabindex', '-1');
        } else if (!skipSrHeading) {
            alert.setAttribute('aria-labelledby', titleID);
            const labelElement = document.createElement('h2');
            labelElement.classList.add('sr-only');
            labelElement.setAttribute('id', titleID);
            labelElement.textContent = this.alertConfig.labelText;
            alert.appendChild(labelElement);
        }

        if (this.alertConfig.type === 'dialog') {
            innerList.setAttribute('role', 'listbox');
            innerList.setAttribute('aria-orientation', 'vertical');
            innerList.setAttribute('tabindex', '-1');
            alert.appendChild(innerList);
        }

        if (this.alertConfig.customCSSClass?.length) {
            this.alertConfig.customCSSClass.forEach(cssClass => alert.classList.add(cssClass));
        }

        const { icon } = this.alertConfig;
        if (icon) {
            const iconElement = document.createElement('div');
            iconElement.classList.add('alert-dialog-icon');
            if (icon === 'default') {
                iconElement.innerHTML = this.checkIcon;
            } else {
                iconElement.innerHTML = icon;
            }

            alert.appendChild(iconElement);
        }

        if (this._kbdMode) {
            document.body.classList.add(KBD_CLASS);
        }

        if (this.alertConfig?.items) {
            this.alertConfig.items.forEach(item => {
                // Build the row/option container
                const row = document.createElement('div');
                const isDialog = this.alertConfig.type === 'dialog';

                row.classList.add('alert-dialog-text');

                if (item.customCSSClass?.length) {
                    item.customCSSClass.forEach(c => row.classList.add(c));
                }

                if (item.attributes) {
                    Object.entries(item.attributes).forEach(([k, v]) => row.setAttribute(k, v));
                }

                if (item.icon) {
                    const iconWrap = document.createElement('span');
                    iconWrap.classList.add('alert-inner-icon');
                    iconWrap.setAttribute('aria-hidden', 'true'); // decorative
                    if (typeof item.icon === 'string') {
                        iconWrap.innerHTML = item.icon;
                    } else {
                        iconWrap.appendChild(item.icon);
                    }
                    alert.appendChild(iconWrap);
                }

                // Visible label text (readable by SR)
                const labelSpan = document.createElement('span');
                labelSpan.classList.add('alert-option-label');
                labelSpan.textContent = item.text;
                row.appendChild(labelSpan);

                if (this.alertConfig.type === 'alert') {
                    row.setAttribute('aria-hidden', 'true');
                }

                // Append to correct container
                if (isDialog) {
                    innerList.appendChild(row);
                } else {
                    alert.appendChild(row);
                }

                // Pointer selection (keeps your existing callback + close)
                row.addEventListener('click', () => {
                    if (row.getAttribute('aria-disabled') === 'true') {
                        return;
                    }

                    if (typeof item.callback === 'function') {
                        item.callback();
                    }

                    this.closeAlert({ restoreFocus: false });
                });

                // CTAs must NOT be children of listbox; append to alert container
                if (item?.cta?.length) {
                    const ctaContainer = document.createElement('div');
                    ctaContainer.classList.add('alert-dialog-ctas');

                    item.cta.forEach(cta => {
                        const btn = document.createElement('button');
                        btn.type = 'button';
                        if (cta.ariaLabel) {
                            btn.setAttribute('aria-label', cta.ariaLabel);

                            const span = document.createElement('span');
                            span.textContent = cta.text;
                            span.setAttribute('aria-hidden', 'true');
                            btn.appendChild(span);
                        } else {
                            btn.textContent = cta.text;
                        }
                        if (typeof cta.callback === 'function') {
                            btn.addEventListener('click', e => {
                                e.stopPropagation();
                                cta.callback();
                                this.closeAlert({ restoreFocus: false });
                            });
                        }
                        ctaContainer.appendChild(btn);
                    });

                    // Always outside the listbox to keep listbox children = only options
                    alert.appendChild(ctaContainer);
                }
            });
        }

        alert.addEventListener('pointerenter', () => {
            this.pauseTimer();
        });

        alert.addEventListener('pointerleave', () => {
            this.startTimer();
        });

        alert.addEventListener('focusin', () => {
            this.pauseTimer();
        });

        alert.addEventListener('focusout', (e) => {
            if (e.relatedTarget && alert.contains(e.relatedTarget)) return;

            const listbox = queryFirst('[role="listbox"]', alert);
            if (listbox) {
                const opts = queryAll('[role="option"]', listbox);
                opts.forEach(o => o.removeAttribute('aria-selected'));
                listbox.removeAttribute('aria-activedescendant');
            }

            this.startTimer(); // по blur
        });

        // close on ESC when focus is somewhere inside alert
        this._onAlertKeydown = (e) => {
            if (e.key !== EVENT_KEY_ESC) return;
            if (!this.alert || !document.body.contains(this.alert)) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            this.closeAlert({ instant: true, restoreFocus: true });
        };
        alert.addEventListener('keydown', this._onAlertKeydown);

        this._onEscPreferAlert = (e) => {
            if (e.key !== EVENT_KEY_ESC) return;
            if (!this.alert || !document.body.contains(this.alert)) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            this._kbMode = true;
            this.closeAlert({ restoreFocus: true });
        };

        // close on scroll (design requirement) – do not move focus
        if (!this.alertConfig?.remainOnScroll) {
            this._scrollTargets = Array.from(queryAll(SCROLLABLE_IN_OVERLAY));

            if (!this._scrollTargets.length) {
                this._scrollTargets = [window];
            } else {
                this._scrollTargets.push(window);
            }
            this._onAnyScroll = () => this.closeAlert({
                instant: true,
                restoreFocus: false
            });
            this._scrollTargets.forEach(t => {
                try { t.addEventListener('scroll', this._onAnyScroll, { passive: true }); }
                catch { t.addEventListener('scroll', this._onAnyScroll); }
            });
        }

        this.alertHost.insertAdjacentElement('beforeend', alert); // FIFO
        this.alert = alert;

        if (!alert.hasAttribute('tabindex') && this.alertConfig.type !== 'alert') {
            alert.setAttribute('tabindex', '-1');
        }

        if (this.alertConfig.type === 'dialog') {
            const listbox = queryFirst('[role="listbox"]', alert);
            if (listbox) {
                // Ensure options are valid for listbox
                const options = queryAll('[role="option"]', listbox);
                options?.forEach(opt => {
                    if (opt.hasAttribute('tabindex')) opt.removeAttribute('tabindex');
                    if (!opt.id) opt.id = `opt_${this.generateRandomString()}`;
                });

                if (options && options.length) {
                    this.setActiveOption(listbox, options[0], true);
                }

                this.bindListboxControls(listbox);
            }
        }

        let firstFocusable =
            queryFirst('a, button', alert) ||
            queryFirst('[role="listbox"]', alert) ||
            alert;

        this.startTimer();

        if (firstFocusable) {
            firstFocusable.focus();
        }
    }

    startTimer() {
        clearTimeout(this.timeout);

        const timeout = this.alertConfig?.alertTimeout ?? 2500;
        if (!timeout) return;

        this.timeout = setTimeout(() => {
            this._kbMode = false;
            this.closeAlert({ restoreFocus: false });
        }, timeout);
    }

    pauseTimer() {
        clearTimeout(this.timeout);
    }

    closeAlert(opts = {}) {
        if (!this.alert || this._isClosing) return;
        this._isClosing = true;

        const { instant = false, restoreFocus = false } = opts;

        this.unbindListboxControls?.();

        if (this._scrollTargets?.length) {
            this._scrollTargets.forEach(t => {
                try { t.removeEventListener('scroll', this._onAnyScroll); } catch {}
            });
        }
        this._scrollTargets = [];
        this._onAnyScroll = null;

        clearTimeout(this.timeout);
        AlertDialog._open.delete(this);

        const el = this.alert;
        this.alert = null;

        if (el && this._onAlertKeydown) {
            el.removeEventListener('keydown', this._onAlertKeydown);
        }
        this._onAlertKeydown = null;

        if (el && el.parentNode) {
            if (instant) {
                el.style.transition = 'none';
                el.style.animation = 'none';
                void el.offsetHeight;
                el.parentNode.removeChild(el);
            } else {
                el.classList.add('m-onclose');
                setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 150);
            }
        }

        if (restoreFocus) {
            const ret = this.alertConfig?.focusAfterClose;
            if (ret instanceof HTMLElement) {
                ret.focus();
            }
        }
    }

    generateRandomString() {
        return (Math.random() + 1).toString(36).substring(3);
    }

    bindListboxControls(listbox) {
        this._onListboxClick = (e) => {
            const opt = e.target.closest('[role="option"]');
            if (!opt) return;
            this.setActiveOption(listbox, opt);
            opt.click();
        };
        listbox.addEventListener('click', this._onListboxClick);

        this._onListboxKeydown = (e) => {
            if (!this.alert || !document.body.contains(this.alert)) return;

            const k = e.key;

            if (k === EVENT_KEY_DOWN || k === EVENT_KEY_UP || k === EVENT_KEY_HOME || k === EVENT_KEY_END) {
                e.preventDefault();
                e.stopPropagation();
                this.moveListboxActive(listbox, k);
                return;
            }

            if (k === EVENT_KEY_ENTER || k === EVENT_KEY_SPACE) {
                const withinListbox = document.activeElement && listbox.contains(document.activeElement);
                if (!withinListbox) return;

                e.preventDefault();
                e.stopPropagation();
                this.selectListboxActive(listbox);
            }
            // do not touch Tab or other keys
        };
        listbox.addEventListener('keydown', this._onListboxKeydown);

        this._onListboxPointerEnter = () => {
            const opts = Array.from(queryAll('[role="option"]', listbox));
            opts.forEach(o => o.removeAttribute('aria-selected'));
            listbox.removeAttribute('aria-activedescendant');
        };
        listbox.addEventListener('pointerenter', this._onListboxPointerEnter);
    }

    unbindListboxControls() {
        const listbox = this.alert && queryFirst('[role="listbox"]', this.alert);
        if (listbox && this._onListboxClick) {
            listbox.removeEventListener('click', this._onListboxClick);
        }
        if (listbox && this._onListboxKeydown) {
            listbox.removeEventListener('keydown', this._onListboxKeydown);
        }
        if (listbox && this._onListboxPointerEnter) {
            listbox.removeEventListener('pointerenter', this._onListboxPointerEnter);
        }
        this._onListboxClick = null;
        this._onListboxKeydown = null;
        this._onListboxPointerEnter = null;
    }

    // Keeps aria-activedescendant in sync; optionally updates aria-selected for keyboard
    setActiveOption(listbox, opt, updateSelected = false) {
        listbox.setAttribute('aria-activedescendant', opt.id);

        if (updateSelected) {
            const opts = Array.from(queryAll('[role="option"]', listbox));
            opts.forEach(o => o.setAttribute('aria-selected', String(o === opt)));
        }

        // If the list scrolls, keep active option in view
        opt.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    moveListboxActive(listbox, key) {
        const opts = Array.from(queryAll('[role="option"]', listbox));
        if (!opts.length) return;

        const activeId = listbox.getAttribute('aria-activedescendant');
        let i = Math.max(0, opts.findIndex(o => o.id === activeId));

        if (i < 0) {
            i = 0;
        }

        if (key === EVENT_KEY_DOWN) {
            i = (i + 1) % opts.length;
        } else if (key === EVENT_KEY_UP) {
            i = (i - 1 + opts.length) % opts.length;
        } else if (key === EVENT_KEY_HOME) {
            i = 0;
        } else if (key === EVENT_KEY_END) {
            i = opts.length - 1;
        }

        this.setActiveOption(listbox, opts[i], true);
    }


    selectListboxActive(listbox) {
        const id = listbox.getAttribute('aria-activedescendant');
        const opt = id && document.getElementById(id);
        if (!opt) return;

        const isDisabled = opt.getAttribute('aria-disabled') === 'true';

        if (isDisabled) {
            if (typeof window.announceMessage === 'function') {
                const textEl = opt.querySelector('.alert-option-label');
                const listName = (textEl && textEl.textContent) || opt.textContent.trim();

                announceMessage(
                    listName
                        ? `This product is already in this wish list: ${listName}`
                        : 'This product is already in this wish list'
                );
            }

            return;
        }

        opt.click(); // triggers item.callback + closeAlert
    }

}
